/**
 * Permanent proxy URLs for email signature assets.
 *
 * v2.16.38: email signatures (compose + campaigns) used to bake in 1h
 * S3 presigned URLs for {{SenderPhoto}} / {{CompanyLogo}}. Recipients
 * opening the email more than an hour later saw a broken image. These
 * helpers emit a permanent backend proxy URL instead — the proxy
 * regenerates a fresh presigned URL on every request, so the bytes
 * stay reachable for as long as the underlying S3 object exists.
 *
 * Keep this file in sync with backend/routes/signatures.py
 * (_signature_photo_url / _signature_logo_url) — both paths must agree
 * so it doesn't matter whether the merge vars are resolved server- or
 * client-side.
 */
// The host these links were built on before #970's QA fix. The runtime image is
// built with REACT_APP_BACKEND_URL="", so this fallback made every tenant's
// "permanent" photo link point at production, where the user does not exist.
const LEGACY_API_BASE = "https://api.nurturecrm.ai";

// The link ends up in sent mail, so it must be absolute. The SPA calls its API
// on its own origin (every other module falls back to ''), so that origin is
// this runtime's API host on every tenant.
export const API_BASE = process.env.REACT_APP_BACKEND_URL
    || (typeof window !== "undefined" && window.location && window.location.origin)
    || LEGACY_API_BASE;

const LEGACY_ASSET_PROXY = /https:\/\/api\.nurturecrm\.ai(\/api\/public\/(?:user-photo|user-logo|org-logo)\/)/g;

/**
 * Point asset proxy links saved on the legacy host at this runtime instead, so
 * signatures saved before the fix heal without a migration. Applied wherever a
 * stored signature is read: `resolveSigVars` (every composer, the campaign
 * modal, the admin preview), the builder's load, and the campaign signature
 * block. A no-op when this runtime IS the legacy host.
 */
export function rebaseSignatureAssetUrls(html) {
    if (!html || typeof html !== "string" || API_BASE === LEGACY_API_BASE) return html;
    return html.replace(LEGACY_ASSET_PROXY, (_m, path) => `${API_BASE}${path}`);
}

const isExternalUrl = (v) =>
    typeof v === "string" && (v.startsWith("http://") || v.startsWith("https://"));

/** Proxy URL for the current user's profile photo, or "" if none. */
function senderPhotoUrl(user) {
    if (!user?.user_id) return "";
    const hasS3 = !!user.picture_s3_key;
    if (hasS3 || isExternalUrl(user.picture)) {
        return `${API_BASE}/api/public/user-photo/${user.user_id}`;
    }
    return "";
}

/** Proxy URL for the current user's company logo. */
function companyLogoUrl(user) {
    if (!user?.user_id) return "";
    const hasS3 = !!user.company_logo_s3_key;
    if (hasS3 || isExternalUrl(user.company_logo_url)) {
        return `${API_BASE}/api/public/user-logo/${user.user_id}`;
    }
    return "";
}

/** Proxy URL for the current user's org logo (tenant-level branding). */
function orgLogoUrl(user) {
    const orgId = user?.org_id;
    if (!orgId) return "";
    // org_logo_url on /me is a presigned URL; org_logo_s3_key isn't exposed
    // to the frontend, so we fall back on the presence of org_logo_url
    // (which implies the backend has either an s3_key or a direct URL).
    if (isExternalUrl(user.org_logo_url)) {
        return `${API_BASE}/api/public/org-logo/${orgId}`;
    }
    return "";
}

/**
 * Merge-map key/value pair for {{SenderPhoto}} + {{CompanyLogo}}. The org
 * logo ({{OrgLogo}}) is folded into {{CompanyLogo}} as a fallback — there is
 * no standalone {{OrgLogo}} merge key. Most callers merge these into a larger
 * merge_map for
 * signature resolution; this helper keeps every signature resolution
 * point in sync — the shared lib/signatureMergeVars.js (serving the
 * inbox and contact compose lanes, #510), EmailEditorModal,
 * SequenceEditor, and SignatureEditor.
 */
export function signatureAssetMergeMap(user) {
    return {
        "{{SenderPhoto}}": senderPhotoUrl(user),
        "{{CompanyLogo}}": companyLogoUrl(user) || orgLogoUrl(user),
    };
}
